import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { LOGIN_RESET, login } from "../actions/auth";
import { DIIA_RESET, login as diiaLogin } from "../actions/diia";
import { styled } from "@mui/material/styles";

import { DiiaLoginPopup } from "./Popup/DiiaLoginPopup";
import { CircularProgress } from "@mui/material";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import PasswordInput from "./Popup/PasswordInput";
import {
  AuthSection1,
  AuthSection2,
  AuthHeader,
  Input,
  ErrorSpan,
  SecondaryText,
  AuthForm,
  AuthBlock,
  AuthHeader2,
} from "../styles/Login.styled";
import { Btn } from "../styles/Button.styled";
import Button from "./Button";
import { FileSignPopup } from "./Popup/FileSignPopup";
import imgLogin from "../../public/i/image/LoginImg.png";
import { DiiaIcon } from "../../public/icons";

export const BpIcon = styled("div")(() => ({
  borderRadius: 5,
  width: 24,
  border: "1px solid var(--login-gray)",
  height: 24,
  padding: 0,
  backgroundColor: "transparent",
}));

export default function Login() {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state: { auth: any }) => state.auth);
  const diia = useAppSelector((state: { diia: any }) => state.diia);
  const user = useAppSelector((state: { user: any }) => state.user);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [autoComplete, setAutoComplete] = useState("on");
  const [activeButton, setActiveButton] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showFilePopup, setFileShowPopup] = useState(false);
  const isDiiaEnabled = process.env.DIIA_ENABLED === "true";

  const clickDiiaLogin = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();
      setUsername("");
      setPassword("");
      dispatch({ type: LOGIN_RESET });
      dispatch(diiaLogin());
      setShowPopup(true);
    },
    [auth],
  );

  const clickFileLogin = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();
      setUsername("");
      setPassword("");
      dispatch({ type: LOGIN_RESET });
      setFileShowPopup(true);
    },
    [auth],
  );

  const closePopup = useCallback(() => {
    dispatch({ type: DIIA_RESET });
    setShowPopup(false);
  }, []);

  const closeFilePopup = useCallback(() => {
    setFileShowPopup(false);
    dispatch({ type: LOGIN_RESET });
  }, []);

  const handleSubmit = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault();
      // const data = new FormData(e.currentTarget);
      // const credentials = Object.fromEntries(data.entries());
      if (username.length > 0 && password.length > 0) {
        dispatch(login({ login: username, password, remember }));
      }
    },
    [auth, username, password, remember],
  );

  const loginChange = useCallback(
    (e: { target: { value: any } }) => {
      setUsername(e.target.value);
      if (auth.state === "failure") {
        dispatch({ type: LOGIN_RESET });
      }
    },
    [auth, dispatch],
  );

  const passwordChange = useCallback(
    (e: { target: { value: any } }) => {
      setPassword(e.target.value);
      setAutoComplete("off");
      if (auth.state === "failure") {
        dispatch({ type: LOGIN_RESET });
      }
    },
    [auth.state, dispatch],
  );

  function BpCheckbox(props: CheckboxProps) {
    return (
      <Checkbox
        sx={{
          "& .MuiSvgIcon-root": {
            borderRadius: 5,
            width: "28px",
            height: "28px",
            borderColor: "var(--disabled-c)",
          },
          "&:hover": { bgcolor: "transparent" },
          "&.Mui-checked": {
            color: "var(--blue)",
            padding: 0,
            width: "24px",
            height: "24px",
          },
          padding: 0,
          marginRight: 1,
        }}
        disableRipple
        color="default"
        icon={<BpIcon />}
        inputProps={{ "aria-label": "Checkbox demo" }}
        {...props}
      />
    );
  }

  const redirect = localStorage.getItem("redirectAfterLogin");

  useEffect(() => {
    if (auth.state === "success") {
         window.location.assign(redirect || "/documents");
         localStorage.removeItem("redirectAfterLogin");
    }
  }, [auth, user.id]);

  useEffect(() => {
    setActiveButton(
      auth.state !== "loading" && !!username.length && !!password.length,
    );
  }, [auth, username, password]);

  return (
    <AuthBlock>
      <AuthSection1>
        <div>
          <AuthHeader>
            Вітаємо в <span style={{ color: "var(--blue)" }}>e</span>Doc
          </AuthHeader>
          <AuthHeader2>
            Авторизуйтесь в eDoc зручним для Вас способом
          </AuthHeader2>
          <AuthForm
            onKeyDown={(e: { key: string }) => {
              if (e.key === "Enter") {
                handleSubmit(new SubmitEvent("submit"));
              }
            }}
          >
            <input
              role="presentation"
              name="_"
              autoComplete="off"
              style={{ display: "none" }}
            />
            <Input
              name="login"
              type="text"
              value={username}
              placeholder="Логін"
              required
              onChange={loginChange}
              maxLength={30}
              $heightMobile={"40px"}
              $activeinput={username.length > 0}
            />
            <PasswordInput
              passwordChange={passwordChange}
              $marginBottom={"0"}
              value={password}
              $heightMobile={"46px"}
              autoComplete={
                autoComplete === "off" ? "new-password" : autoComplete
              }
              activeButton={password?.length > 0}
            />
            <ErrorSpan>
              {auth.state === "failure" && !showFilePopup
                ? auth.result.message
                : null}
            </ErrorSpan>
            <FormControlLabel
              style={{ marginLeft: "0" }}
              control={
                <BpCheckbox
                  onChange={(e: { currentTarget: { checked: any } }) => {
                    setRemember(e.currentTarget.checked);
                  }}
                  checked={remember}
                  name="remember"
                />
              }
              name="remember"
              label={<SecondaryText>Запам&#39;ятати мене</SecondaryText>}
            />

            <Button
              title={
                auth.state === "loading" ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "var(--white)",
                    }}
                  />
                ) : (
                  "Продовжити"
                )
              }
              $type="submit"
              $heightMobile={"47px"}
              onClick={handleSubmit}
              disabled={!activeButton}
              $disabledC="white"
              $width="100%"
              $marginTop="0"
              bgcolor="blue"
              disabledColor="var(--disabled-btn-bg)"
            />
            <div style={{ borderTop: "1px solid var(--disabled-c)" }} />
            <div style={{ display: "flex", gap: "10px" }}>
              {isDiiaEnabled && (
                <Btn
                  type="button"
                  $background="var(--black)"
                  onClick={clickDiiaLogin}
                  disabled={diia.state === "loading"}
                  $width="100%"
                  $heightMobile={"47px"}
                  onKeyDown={(e: {
                    key: string;
                    stopPropagation: () => void;
                  }) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "8px",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <DiiaIcon />
                    <div>Вхід через Дію</div>
                  </div>
                </Btn>
              )}
              <Btn
                type="button"
                $background="var(--orange)"
                onClick={clickFileLogin}
                $width="100%"
                $heightMobile={"47px"}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div>КЕП</div>
                </div>
              </Btn>
            </div>
          </AuthForm>
        </div>
      </AuthSection1>
      <AuthSection2>
        <img
          src={imgLogin}
          style={{ width: "100%", height: "80vh", maxHeight: "800px" }}
          alt=""
        />
      </AuthSection2>
      {showPopup ? (
        <DiiaLoginPopup clickCloseButton={closePopup} diia={diia} />
      ) : null}

      {showFilePopup && (
        <FileSignPopup login={true} clickCloseButton={closeFilePopup} />
      )}
    </AuthBlock>
  );
}
